
import PageTitle from "@/components/PageTitle.vue";
import axios from "axios";
import {uppercase} from "@/helpers/strings";
import {date, humanDate} from "@/helpers/date";
import ProductInfo from "@/components/ProductInfo.vue";
import _ from "lodash";
import {ModelSelect} from "vue-search-select/src/lib";
import moment from "moment";
import {OmsLocation} from "@/declarations/models";

type DispatchSuggestionResponseItem = {
    carrier: {
        display_name: string;
    },
    delivery_date: string;
    responsible_location_identifier: string;
    stock_level: number;
}

type DispatchSuggestionMapped = {
    carrier: string;
    responsible_location_identifier: string;
    responsible_location_display_name: string;
    delivery_date: string;
    stock_level: number;
}

type ModelSelectOption = {
    text: string;
    value: string;
}

export default {
    name: 'SleQuickSuggestion',
    components: {ProductInfo, PageTitle, ModelSelect},

    data() {
        return {
            isProcessing: false as boolean,
            hasSuggestionResponse: false as boolean,
            isDeliveryDetailsModalOpen: false as boolean,
            isEditZipcodeModalOpen: false as boolean,
            isEditPickupLocationIdentifierModalOpen: false as boolean,
            form: {
                productIdentifier: '' as string,
                zipcode: '' as string,
                quantity: 1 as number,
                zipcodeInModal: '' as string,
                pickupLocationIdentifier: null as string | null,
            },

            isLoadingSuggestion: {
                delivery: false as boolean,
                pickup: false as boolean,
            },
            isLoadingSuggestionError: {
                delivery: false as boolean,
                pickup: false as boolean,
            },

            suggestions: {
                delivery: [],
                pickup: [],
            },

            fulfilmentMethods: ['delivery', 'pickup'],
        }
    },

    async mounted(): void {
        this.form.zipcode = localStorage.getItem('quickSuggestion.zipcode') ?? ''
        this.form.pickupLocationIdentifier = this.locationForUser?.identifier

        await this.$store.getters.getAllLocations

        if (this.$route.params.productIdentifier) {
            this.form.productIdentifier = this.$route.params.productIdentifier
            this.getSuggestions()
        }
    },

    computed: {
        productImage(): string {
            return this.$store.state.products[this.form.productIdentifier]
                ?.attribute_values.find(val => val.product_attribute?.identifier === 'image_url')?.value
        },
        locationForUser(): OmsLocation {
            return this.$store.state.locationsForUser[0]
        },
        deliverySuggestion(): DispatchSuggestionMapped {
            return this.suggestions.delivery[0]
        },
        possiblePickupLocations(): ModelSelectOption {
            return _.chain(this.$store.state.allLocations)
                .filter(l => l.location_type.identifier === 'store' && parseInt(l.active))
                .sortBy('display_name')
                .map(l => ({
                    text: l.display_name,
                    value: l.identifier,
                }))
                .value()
        },
    },

    methods: {
        date,
        humanDate,
        uppercase,
        getSuggestions(): void {
            this.isProcessing = true;
            this.hasSuggestionResponse = false;
            const promises = [
                this.$store.getters.getProductInfo(this.form.productIdentifier)
                    .catch(e => {}),
                this.getSuggestionForFulfillmentMethod('pickup')
            ];

            if (this.form.zipcode) {
                promises.push(
                    this.getSuggestionForFulfillmentMethod('delivery')
                )
            }

            Promise.all(promises)
                .then(() => {
                    this.hasSuggestionResponse = true
                    this.$root.$emit('bv::toggle::collapse', `results`)
                })
                .catch(e => {
                    this.$alert(`Fout bij het ophalen van suggesties`, 'danger')
                })
                .finally(() => {
                    this.isProcessing = false;
                })
        },
        async getSuggestionForFulfillmentMethod(method: string): Promise {
            this.isLoadingSuggestion[method] = true;
            this.isLoadingSuggestionError[method] = false;

            try {
                const response = await axios.post(`/api/suggestion/product/${this.form.productIdentifier}`, {
                    quantity: this.form.quantity,
                    fulfilment_method: method,
                    zipcode: this.form.zipcode,
                    location_identifier: this.form.pickupLocationIdentifier,
                })

                this.isLoadingSuggestion[method] = false;

                this.suggestions[method] = response.data.map((suggestion: DispatchSuggestionResponseItem)  => ({
                    carrier: suggestion.carrier?.display_name,
                    responsible_location_identifier: suggestion.responsible_location_identifier,
                    responsible_location_display_name: this.getLocation(suggestion.responsible_location_identifier)?.display_name,
                    delivery_date: suggestion.delivery_date,
                    stock_level: suggestion.stock_level,
                }))

                this.suggestions[method] = _.orderBy(
                    this.suggestions[method],
                    ['responsible_location_display_name'],
                    ['asc'],
                )
            } catch (e: Error) {
                this.isLoadingSuggestion[method] = false;
                this.isLoadingSuggestionError[method] = true
            }
        },
        getLocation(locationIdentifier: string): OmsLocation {
            return this.$store.state.allLocations.find(l => l.identifier === locationIdentifier)
        },
        editZipcode(): void {
            this.form.zipcode = this.form.zipcodeInModal
            localStorage.setItem('quickSuggestion.zipcode', this.form.zipcode)
            this.form.zipcodeInModal = ''
            this.isEditZipcodeModalOpen = false
            this.getSuggestionForFulfillmentMethod('delivery')
        },
        editPickupLocationIdentifier(): void {
            this.isEditPickupLocationIdentifierModalOpen = false
            this.getSuggestionForFulfillmentMethod('pickup')
        },
        pickupDateToHumanText(date): string {
            moment.locale('nl')
            const momentDate = new moment(date)
            return momentDate.calendar(null, {
                sameDay: '[vandaag]',
                nextDay: '[morgen]',
                nextWeek: 'D MMMM YYYY',
                sameElse: 'D MMMM YYYY'
            })
        }
    },
}
